import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ReceiptText,
  UserPlus,
  Plus,
  Minus,
  Trash2,
  Banknote,
  Smartphone,
  CreditCard,
  CheckCircle2,
  ChevronRight,
  Users,
  Search,
  ShoppingCart,
  User,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole, useAuth } from "@/lib/auth-store";
import {
  tabStore,
  useTabStore,
  type Tab,
  type TabPayment,
} from "@/lib/tab-store";
import { useStore } from "@/lib/pos-store";
import { useRealtimeTabs, markLocalAction } from "@/hooks/useRealtimeTabs";

export const Route = createFileRoute("/tabs-waiter")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "waiter")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "My Customers — Waiter" },
      {
        name: "description",
        content: "Manage your customer tabs.",
      },
    ],
  }),
  component: TabsWaiterPage,
});

const paymentMethods: {
  value: TabPayment["method"];
  label: string;
  icon: typeof Banknote;
}[] = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "mpesa", label: "M-Pesa", icon: Smartphone },
  { value: "bank", label: "Bank", icon: CreditCard },
];

type CartLine = {
  itemId: string;
  name: string;
  qty: number;
  unitPrice: number;
};

function TabsWaiterPage() {
  const { user } = useAuth();
  const waiterId = user?.id ?? "";
  const allTabs = useTabStore((s) => s.tabs);

  // Listen for real-time updates from other clients
  useRealtimeTabs();

  // Load waiter tabs when component mounts
  useEffect(() => {
    tabStore.initWaiter();
  }, []);
  const items = useStore((s) => s.items);
  const navigate = useNavigate();

  // My open tabs
  const myOpenTabs = allTabs.filter(
    (t) => t.waiterId === waiterId && t.status === "open",
  );
  // My closed tabs (history)
  const myClosedTabs = allTabs.filter(
    (t) => t.waiterId === waiterId && t.status === "closed",
  );

  const [searchQ, setSearchQ] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerDialog, setNewCustomerDialog] = useState(false);

  // Tab detail
  const [detailTab, setDetailTab] = useState<Tab | null>(null);

  // Payment
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<TabPayment["method"]>("cash");
  const [payDialog, setPayDialog] = useState(false);

  // ── Menu / Cart states ───────────────────────────────────────────────────
  const [menuTab, setMenuTab] = useState<Tab | null>(null);
  const [menuSearch, setMenuSearch] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);

  const filteredMenu = useMemo(
    () =>
      items.filter(
        (i) =>
          i.stock > 0 &&
          (i.name.toLowerCase().includes(menuSearch.toLowerCase()) ||
            i.sku.toLowerCase().includes(menuSearch.toLowerCase())),
      ),
    [items, menuSearch],
  );

  const cartTotal = cart.reduce(
    (a, l) => a + Number(l.qty) * Number(l.unitPrice),
    0,
  );

  const addToCart = (itemId: string) => {
    setCart((c) => {
      const ex = c.find((l) => l.itemId === itemId);
      if (ex)
        return c.map((l) =>
          l.itemId === itemId ? { ...l, qty: Number(l.qty) + 1 } : l,
        );
      const it = items.find((i) => i.id === itemId)!;
      return [
        ...c,
        { itemId, name: it.name, qty: 1, unitPrice: it.sellingPrice },
      ];
    });
  };

  const handleSubmitOrder = async () => {
    if (!menuTab || cart.length === 0) return;
    markLocalAction();
    const updated = await tabStore.waiterBatchAddToTab(menuTab.id, cart);
    if (updated) {
      toast.success(
        `${cart.reduce((a, l) => a + Number(l.qty), 0)} item(s) added to ${menuTab.customerName}'s bill`,
      );
      setCart([]);
      setMenuSearch("");
      setMenuTab(null);
      if (detailTab?.id === menuTab.id) {
        setDetailTab(
          tabStore.getState().tabs.find((t) => t.id === menuTab.id) ?? null,
        );
      }
    }
  };

  const handleQuickOrder = (tab: Tab) => {
    setMenuTab(tab);
    setCart([]);
    setMenuSearch("");
  };

  // ── Pay / Close ──────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!detailTab || !payAmount || +payAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    markLocalAction();
    const updated = await tabStore.waiterPayTab(
      detailTab.id,
      +payAmount,
      payMethod,
    );
    if (updated) {
      toast.success(`Payment of ${fmt(+payAmount)} recorded`);
      setPayAmount("");
      setPayDialog(false);
      setDetailTab(
        tabStore.getState().tabs.find((t) => t.id === detailTab.id) ?? null,
      );
    }
  };

  const handleCloseTab = async () => {
    if (!detailTab) return;
    const balance =
      Number(detailTab.items.reduce((a, i) => a + Number(i.total), 0)) -
      Number(detailTab.payments.reduce((a, p) => a + Number(p.amount), 0));
    if (balance > 0) {
      toast.error(`Customer still owes ${fmt(balance)}`);
      return;
    }
    markLocalAction();
    await tabStore.waiterCloseTab(detailTab.id);
    toast.success(`${detailTab.customerName}'s bill closed`);
    setDetailTab(null);
  };

  const handleCreateTab = async () => {
    const name = newCustomerName.trim();
    if (!name) {
      toast.error("Enter customer name");
      return;
    }
    markLocalAction();
    const tab = await tabStore.waiterCreateTab(name);
    if (tab) {
      toast.success(`Bill opened for ${name}`);
      setNewCustomerName("");
      setNewCustomerDialog(false);
      handleQuickOrder(tab);
    }
  };

  /** Print an invoice for a tab (works for open or closed tabs) */
  const handlePrintInvoice = (tab: Tab) => {
    const printWin = window.open("", "_blank", "width=400,height=600");
    if (!printWin) return;

    const total = Number(tab.items.reduce((a, i) => a + Number(i.total), 0));
    const paid = Number(tab.payments.reduce((a, p) => a + Number(p.amount), 0));
    const balance = total - paid;

    const fmtHtml = (n: number) =>
      new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        maximumFractionDigits: 0,
      }).format(n);

    const itemsHtml = tab.items
      .map(
        (item) => `
        <tr>
          <td style="padding:3px 0;">
            ${item.name}
            <br><span style="color:#888;font-size:10px;">${Number(item.qty)} × ${fmtHtml(Number(item.unitPrice))}</span>
          </td>
          <td style="text-align:right;padding:3px 0;white-space:nowrap;">${fmtHtml(Number(item.total))}</td>
        </tr>`,
      )
      .join("");

    const paymentsHtml = tab.payments
      .map(
        (p) => `
        <tr>
          <td style="padding:3px 0;text-transform:capitalize">${p.method}</td>
          <td style="text-align:right;padding:3px 0;white-space:nowrap;">${fmtHtml(Number(p.amount))}</td>
        </tr>`,
      )
      .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice</title>
  <style>
    @page { margin: 0; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      margin: 0;
      padding: 32px 16px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      background: white;
      color: black;
    }
    .invoice-body { width: 80mm; max-width: 100%; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .line { border-top: 1px dashed #999; margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; }
    td:last-child { text-align: right; white-space: nowrap; }
    .total td { padding-top: 4px; font-size: 14px; font-weight: bold; }
    .invoice-badge {
      display: inline-block;
      border: 2px solid #333;
      padding: 2px 12px;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: 2px;
      margin-bottom: 4px;
    }
  </style>
</head>
<body>
  <div class="invoice-body">
    <div class="center bold" style="font-size:14px;">White House Lounge</div>
    <div class="center"><span class="invoice-badge">INVOICE</span></div>
    <div class="center" style="color:#888;font-size:10px;">#${tab.id.slice(-6).toUpperCase()}</div>
    <div class="line"></div>
    <div>Bill To: <strong>${tab.customerName}</strong></div>
    <div>Date: ${new Date(Number(tab.createdAt)).toLocaleString()}</div>
    ${tab.waiterId ? `<div>Waiter ID: ${tab.waiterId}</div>` : ""}
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Description</td><td>Amount</td></tr>
      ${itemsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr><td>Subtotal</td><td>${fmtHtml(total)}</td></tr>
      ${
        tab.payments.length > 0
          ? `
      <tr class="bold"><td>Payments</td><td></td></tr>
      ${paymentsHtml}
      <tr><td>Total Paid</td><td>${fmtHtml(paid)}</td></tr>
      `
          : ""
      }
      <tr class="total"><td>${balance <= 0 ? "SETTLED" : "AMOUNT DUE"}</td><td>${fmtHtml(Math.abs(balance))}</td></tr>
    </table>
    <div class="line"></div>
    <div class="center" style="color:#888;font-size:10px;">Thank you · Karibu tena</div>
    <div class="center" style="color:#888;font-size:9px;margin-top:4px;">This is a computer-generated invoice.</div>
  </div>
</body>
</html>`;

    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  // ── Filtered open tabs ───────────────────────────────────────────────────
  const filtered = myOpenTabs.filter(
    (t) =>
      t.customerName.toLowerCase().includes(searchQ.toLowerCase()) ||
      t.id.toLowerCase().includes(searchQ.toLowerCase()),
  );

  return (
    <div>
      <PageHeader
        title="My Customers"
        subtitle={`${myOpenTabs.length} open bill${myOpenTabs.length !== 1 ? "s" : ""}`}
        right={
          <Dialog open={newCustomerDialog} onOpenChange={setNewCustomerDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <UserPlus className="h-4 w-4" />
                New Bill
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Open Bill for Customer</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    placeholder="e.g. John Doe"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateTab();
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreateTab}>Open Bill</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search customer..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>
      </div>

      {/* My Open Tabs */}
      <div className="space-y-2 px-4 pt-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">
              No Open Bills
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQ
                ? "No bills match your search"
                : "Open a new bill for a customer"}
            </p>
          </div>
        ) : (
          filtered.map((tab) => {
            const tabTotal = tab.items.reduce((a, i) => a + Number(i.total), 0);
            const tabPaid = tab.payments.reduce(
              (a, p) => a + Number(p.amount),
              0,
            );
            const balance = tabTotal - tabPaid;
            const unissued = tab.items.filter((i) => !i.issued).length;
            return (
              <div
                key={tab.id}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                {/* Main row — tap for detail */}
                <button
                  onClick={() => setDetailTab(tab)}
                  className="w-full p-3 text-left active:scale-[0.98] min-h-[56px]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">
                      {tab.customerName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{tab.customerName}</p>
                      <p className="text-xs text-muted-foreground">
                        {tab.items.reduce((a, i) => a + Number(i.qty), 0)} item
                        {tab.items.reduce((a, i) => a + Number(i.qty), 0) !== 1
                          ? "s"
                          : ""}
                        {unissued > 0 && (
                          <span className="ml-1 text-warning">
                            · {unissued} pending
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-sm font-semibold">
                        {fmt(balance)}
                      </p>
                      <p
                        className={`text-[10px] ${balance <= 0 ? "text-primary" : "text-muted-foreground"}`}
                      >
                        {balance <= 0 ? "PAID" : "OWES"}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>

                {/* Quick Order button */}
                <div className="border-t border-border px-3 py-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="w-full h-9 text-xs gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickOrder(tab);
                    }}
                  >
                    <ShoppingCart className="h-3.5 w-3.5" />
                    Order Items
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Closed tabs history */}
      {myClosedTabs.length > 0 && !searchQ && (
        <section className="px-4 pt-6 pb-24">
          <h2 className="font-display text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">
            Closed Bills
          </h2>
          <div className="space-y-2">
            {myClosedTabs.slice(0, 10).map((tab) => {
              const tabPaid = tab.payments.reduce(
                (a, p) => a + Number(p.amount),
                0,
              );
              return (
                <div
                  key={tab.id}
                  className="rounded-xl border border-border bg-card p-3"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{tab.customerName}</p>
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                      {tab.items.length} items
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {new Date(Number(tab.createdAt)).toLocaleDateString(
                        "en-KE",
                        {
                          day: "numeric",
                          month: "short",
                        },
                      )}
                    </span>
                    <span>Paid: {fmt(tabPaid)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── MENU / CART DIALOG ───────────────────────────────────────────── */}
      <Dialog
        open={menuTab !== null}
        onOpenChange={(v) => {
          if (!v) {
            setMenuTab(null);
            setCart([]);
            setMenuSearch("");
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          {menuTab && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Order for {menuTab.customerName}
                </DialogTitle>
              </DialogHeader>

              {/* Item search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search menu..."
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                />
              </div>

              {/* Menu grid — full visual style like sell.tsx */}
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                {filteredMenu.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => addToCart(it.id)}
                    className="rounded-lg border border-border bg-card p-2.5 text-left active:scale-[0.97] min-h-[64px]"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                      {it.sku}
                    </p>
                    <p className="line-clamp-2 text-xs font-medium leading-snug">
                      {it.name}
                    </p>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="font-display text-xs font-semibold text-primary">
                        {fmt(it.sellingPrice)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {it.stock} left
                      </span>
                    </div>
                  </button>
                ))}
                {filteredMenu.length === 0 && (
                  <p className="col-span-2 py-4 text-center text-sm text-muted-foreground">
                    No items match "{menuSearch}"
                  </p>
                )}
              </div>

              {/* Cart */}
              {cart.length > 0 && (
                <div className="rounded-lg border border-border bg-card">
                  <div className="max-h-40 space-y-1 overflow-y-auto p-2">
                    {cart.map((l) => (
                      <div key={l.itemId} className="flex items-center gap-1.5">
                        <p className="flex-1 truncate text-xs font-medium">
                          {l.name}
                        </p>
                        <button
                          onClick={() =>
                            setCart((c) =>
                              c
                                .map((x) =>
                                  x.itemId === l.itemId
                                    ? {
                                        ...x,
                                        qty: Math.max(0, Number(x.qty) - 1),
                                      }
                                    : x,
                                )
                                .filter((x) => Number(x.qty) > 0),
                            )
                          }
                          className="rounded bg-secondary p-1 min-h-[28px] min-w-[28px] flex items-center justify-center"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-6 text-center text-xs font-bold tabular-nums">
                          {Number(l.qty)}
                        </span>
                        <button
                          onClick={() => addToCart(l.itemId)}
                          className="rounded bg-secondary p-1 min-h-[28px] min-w-[28px] flex items-center justify-center"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                        <span className="w-14 text-right text-xs font-semibold tabular-nums">
                          {fmt(Number(l.qty) * Number(l.unitPrice))}
                        </span>
                        <button
                          onClick={() =>
                            setCart((c) =>
                              c.filter((x) => x.itemId !== l.itemId),
                            )
                          }
                          className="rounded p-1 text-muted-foreground min-h-[28px] min-w-[28px] flex items-center justify-center"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border p-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold">Total</span>
                      <span className="font-display text-sm font-bold">
                        {fmt(cartTotal)}
                      </span>
                    </div>
                    <Button
                      className="w-full h-9 text-xs"
                      onClick={handleSubmitOrder}
                    >
                      <ShoppingCart className="h-3.5 w-3.5" />
                      Add to Bill {fmt(cartTotal)}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── TAB DETAIL DIALOG ─────────────────────────────────────────────── */}
      <Dialog
        open={detailTab !== null}
        onOpenChange={(v) => {
          if (!v) setDetailTab(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          {detailTab && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">
                    {detailTab.customerName.charAt(0).toUpperCase()}
                  </div>
                  {detailTab.customerName}'s Bill
                </DialogTitle>
              </DialogHeader>

              {/* Summary */}
              <div className="rounded-xl bg-gradient-to-br from-primary to-primary/70 p-4 text-primary-foreground">
                <p className="text-xs uppercase tracking-widest opacity-80">
                  Balance
                </p>
                <p className="font-display text-3xl font-bold">
                  {fmt(
                    Number(
                      detailTab.items.reduce((a, i) => a + Number(i.total), 0),
                    ) -
                      Number(
                        detailTab.payments.reduce(
                          (a, p) => a + Number(p.amount),
                          0,
                        ),
                      ),
                  )}
                </p>
                <div className="mt-2 flex gap-3 text-xs opacity-80">
                  <span>
                    Total:{" "}
                    {fmt(
                      Number(
                        detailTab.items.reduce(
                          (a, i) => a + Number(i.total),
                          0,
                        ),
                      ),
                    )}
                  </span>
                  <span>
                    Paid:{" "}
                    {fmt(
                      Number(
                        detailTab.payments.reduce(
                          (a, p) => a + Number(p.amount),
                          0,
                        ),
                      ),
                    )}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-sm font-semibold mb-1">Items Ordered</h4>
                <div className="divide-y divide-border rounded-xl border border-border bg-card">
                  {detailTab.items.map((item, idx) => (
                    <div
                      key={item.itemId + idx}
                      className="flex items-center gap-2 px-3 py-2"
                    >
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-md text-[10px] font-bold ${
                          item.issued
                            ? "bg-primary/10 text-primary"
                            : "bg-warning/10 text-warning"
                        }`}
                      >
                        {Number(item.qty)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{item.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          by {item.serverName}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {fmt(Number(item.total))}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {item.issued ? "✓ Served" : "Pending"}
                        </p>
                      </div>
                    </div>
                  ))}
                  {detailTab.items.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      No items yet
                    </p>
                  )}
                </div>
              </div>

              {/* Order More */}
              <Button
                variant="outline"
                className="w-full h-10 gap-1.5"
                onClick={() => handleQuickOrder(detailTab)}
              >
                <ShoppingCart className="h-4 w-4" />
                Order More Items
              </Button>

              {/* Payments */}
              {detailTab.payments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Payments</h4>
                  <div className="divide-y divide-border rounded-xl border border-border bg-card">
                    {detailTab.payments.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-2 px-3 py-2"
                      >
                        {p.method === "cash" ? (
                          <Banknote className="h-4 w-4 text-muted-foreground" />
                        ) : p.method === "mpesa" ? (
                          <Smartphone className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                        )}
                        <p className="flex-1 text-sm capitalize">{p.method}</p>
                        <p className="text-sm font-semibold text-primary">
                          {fmt(Number(p.amount))}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button
                  variant="outline"
                  className="h-auto py-3 min-h-[48px]"
                  onClick={() => setPayDialog(true)}
                >
                  <Banknote className="h-4 w-4" />
                  <span className="ml-1 text-sm">Record Payment</span>
                </Button>

                {(() => {
                  const closeTotal = Number(
                    detailTab.items.reduce((a, i) => a + Number(i.total), 0),
                  );
                  const closePaid = Number(
                    detailTab.payments.reduce(
                      (a, p) => a + Number(p.amount),
                      0,
                    ),
                  );
                  const closeBalance = closeTotal - closePaid;
                  return (
                    <Button
                      variant={closeBalance <= 0 ? "default" : "secondary"}
                      className="h-auto py-3 min-h-[48px]"
                      disabled={closeBalance > 0}
                      onClick={handleCloseTab}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="ml-1 text-sm">
                        {closeBalance > 0
                          ? `Owes ${fmt(closeBalance)}`
                          : "Close Bill"}
                      </span>
                    </Button>
                  );
                })()}

                <Button
                  variant="outline"
                  className="h-auto py-3 min-h-[48px] col-span-2"
                  onClick={() => handlePrintInvoice(detailTab)}
                >
                  <FileText className="h-4 w-4" />
                  <span className="ml-1 text-sm">Print Invoice</span>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Record Payment for {detailTab?.customerName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="e.g. 500"
                autoFocus
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {paymentMethods.map((pm) => {
                  const Icon = pm.icon;
                  return (
                    <button
                      key={pm.value}
                      onClick={() => setPayMethod(pm.value)}
                      className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-sm font-medium capitalize transition ${
                        payMethod === pm.value
                          ? "border-primary bg-accent text-accent-foreground"
                          : "border-border bg-card text-muted-foreground"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {pm.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {detailTab &&
              (() => {
                const payTotal = Number(
                  detailTab.items.reduce((a, i) => a + Number(i.total), 0),
                );
                const payPaid = Number(
                  detailTab.payments.reduce((a, p) => a + Number(p.amount), 0),
                );
                return (
                  <p className="text-center text-sm text-muted-foreground">
                    Balance: {fmt(payTotal - payPaid)}
                  </p>
                );
              })()}
          </div>
          <DialogFooter>
            <Button onClick={handlePay}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="h-24" />
    </div>
  );
}
